import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Clock, HeartHandshake, Megaphone, ShieldCheck, Sparkles, Sun, Users } from "lucide-react";
import { ENROL_URL, FEES, IMAGES, NQS_ASSESSMENT_DATE } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import NewsletterForm from "../components/NewsletterForm";

const programs = [
  { title: "Before School Care", time: "7:30am - 9:00am", desc: "A calm, gentle start to the school day with breakfast options and quiet play.", icon: Sun, testId: "home-program-bsc" },
  { title: "After School Care", time: "3:15pm - 6:00pm", desc: "Afternoon tea, homework support, sport, craft and unhurried time with friends.", icon: HeartHandshake, testId: "home-program-asc" },
  { title: "School Vacation Care", time: "8:00am - 6:00pm", desc: "Full-day holiday programs with excursions and engaging in-house activities.", icon: Sparkles, testId: "home-program-svc" },
  { title: "Telopea Activities Program", time: "During After School Care", desc: "Paid extracurricular activities - from languages to sport - run on-site during ASC.", icon: Award, testId: "home-program-tap" },
];

const pillars = [
  { icon: HeartHandshake, title: "Warm & welcoming", desc: "Every child is greeted by name and known as an individual, in a space that feels like an extension of home." },
  { icon: ShieldCheck, title: "Safe & professional", desc: "Qualified, consistent educators and strong governance underpin everything we do." },
  { icon: Sparkles, title: "Play with purpose", desc: "Play-based programs shaped by the My Time, Our Place framework and children's own ideas." },
];

const Home = () => (
  <div data-testid="home-page">
    <Seo
      title="Telopea Park OSHC | A Home Away From Home - Barton, Canberra"
      description="Not-for-profit, P&C-run Out of School Hours Care at Telopea Park School, Barton. Before School Care, After School Care and Vacation Care rated Exceeding the National Quality Standard."
      path="/"
    />
    <section className="relative" data-testid="home-hero">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Telopea Park School grounds in Barton, Canberra" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/55 to-navy/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
      </div>
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-36 lg:py-44">
        <FadeIn>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-white/80" data-testid="home-hero-eyebrow">
            Out of School Hours Care - Barton, Canberra
          </p>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white max-w-3xl" data-testid="home-hero-title">
            A Home Away From Home
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-white/85 max-w-2xl" data-testid="home-hero-subtitle">
            Telopea Park OSHC offers warm, professional before school, after school and vacation care where every child
            belongs, plays and thrives.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={ENROL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-hero-enrol-button"
              className="inline-flex items-center gap-2 rounded-full bg-waratah px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Enrol Now <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to="/programs"
              data-testid="home-hero-programs-button"
              className="inline-flex items-center rounded-full border-2 border-white px-8 py-4 text-lg font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Explore Programs
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="bg-surface border-y border-navy-100" data-testid="home-nqs-banner">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-16">
        <FadeIn className="flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-waratah-50 text-waratah">
            <Award className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy" data-testid="home-nqs-title">
              Rated Exceeding the National Quality Standard
            </h2>
            <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed">
              Our most recent assessment on {NQS_ASSESSMENT_DATE} rated Telopea Park OSHC as Exceeding the National
              Quality Standard overall, with Quality Areas 4 to 7 rated Exceeding.
            </p>
          </div>
          <Link
            to="/nqs"
            data-testid="home-nqs-link"
            className="inline-flex shrink-0 items-center gap-2 rounded-full border-2 border-navy px-6 py-3 text-base font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
          >
            Our NQS results <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </section>

    <section className="bg-navy" data-testid="home-pandc-banner">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-16">
        <FadeIn className="flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-waratah-300 ring-1 ring-white/15">
            <Users className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white" data-testid="home-pandc-title">
              A not-for-profit, P&amp;C-run service
            </h2>
            <p className="mt-2 text-base sm:text-lg text-navy-100 leading-relaxed">
              We are one of the few OSHC services in Canberra still run directly by the school&rsquo;s Parents &amp;
              Citizens (P&amp;C) Association &ndash; a community-led, not-for-profit service shaped by Telopea families,
              with every dollar reinvested back into your children.
            </p>
          </div>
          <Link
            to="/about"
            data-testid="home-pandc-link"
            className="inline-flex shrink-0 items-center gap-2 rounded-full border-2 border-white px-6 py-3 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-navy"
          >
            About our P&amp;C <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 sm:pt-24" data-testid="home-announcements">
      <FadeIn>
        <div className="rounded-3xl border border-waratah-200 bg-waratah-50 p-8 sm:p-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-waratah shadow-sm">
                <Megaphone className="h-6 w-6" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-waratah" data-testid="home-announcements-eyebrow">
                  Latest news
                </p>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-navy">
                  Upcoming fee update &amp; school fete
                </h2>
                <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed">
                  Fee changes take effect from December 2026, and Telopea Park OSHC is at La Grande F&ecirc;te on
                  Saturday 31 October. Read the details on our announcements page.
                </p>
              </div>
            </div>
            <Link
              to="/announcements"
              data-testid="home-announcements-link"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-6 py-3 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy-700"
            >
              View announcements <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </FadeIn>
    </section>

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24" data-testid="home-programs">
      <FadeIn>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah">Our Programs</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-navy max-w-2xl">
          Care that wraps around the school day
        </h2>
      </FadeIn>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {programs.map((p, i) => (
          <FadeIn key={p.testId} delay={i * 0.08}>
            <Link
              to="/programs"
              data-testid={p.testId}
              className="group block h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-surface text-navy transition-colors duration-200 group-hover:bg-waratah-50 group-hover:text-waratah">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-navy">{p.title}</h3>
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-waratah">
                <Clock className="h-4 w-4" /> {p.time}
              </p>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{p.desc}</p>
            </Link>
          </FadeIn>
        ))}
      </div>
    </section>

    <section className="bg-surface border-y border-navy-100" data-testid="home-why">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <FadeIn>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah">Why families choose us</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-navy">
              Care that feels like family
            </h2>
            <div className="mt-10 space-y-8">
              {pillars.map((pillar) => (
                <div key={pillar.title} className="flex gap-5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-navy shadow-sm border border-slate-200">
                    <pillar.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-navy">{pillar.title}</h3>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-10 inline-flex items-center gap-2 rounded-full border-2 border-navy px-7 py-3.5 text-base font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
            >
              About our philosophy <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
          <FadeIn delay={0.1}>
            <img
              src={IMAGES.playing}
              alt="Children making bead and pipe-cleaner crafts together at an outdoor table"
              className="w-full rounded-3xl object-cover shadow-lg aspect-[4/3]"
              data-testid="home-why-image"
            />
          </FadeIn>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24" data-testid="home-fees-teaser">
      <FadeIn className="rounded-3xl bg-navy px-8 py-14 sm:p-16 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white" data-testid="home-fees-title">
          Transparent fees, generous subsidies
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80 leading-relaxed">
          After School Care from ${FEES.asc.permanent.toFixed(2)} per session before the Child Care Subsidy. Use our
          interactive estimator to see what your family might pay.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link
            to="/fees"
            data-testid="home-fees-link"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-navy shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            Fees & CCS estimator <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={ENROL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-fees-enrol-button"
            className="inline-flex items-center rounded-full bg-waratah px-8 py-4 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600"
          >
            Enrol Now
          </a>
        </div>
      </FadeIn>
    </section>

    <section className="bg-surface border-t border-navy-100" data-testid="home-newsletter">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 text-center">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-navy">Stay in the loop</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-600 leading-relaxed">
            Occasional email updates about programs, vacation care dates and enrolment news. No spam, ever.
          </p>
          <div className="mx-auto mt-8 max-w-lg">
            <NewsletterForm />
          </div>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default Home;
